import React, { useState, type FC, type FormEventHandler } from 'react';

import { Box } from '@clutter/clean';

import { Panel, Table, Button, NavLinkButton, Alert, Checkbox } from '@shared/components/bootstrap';
import { Picture, Pluralize } from '@shared/components/helpers';

import { WarehouseSelectFormGroup } from '@admin/components/warehouses/select_form_group';

type Barcode = {
  value: string;
};

type Image = {
  id: number;
  state: string;
  source: string;
};

type Item = {
  id: number;
  hero: number;
  images: Image[];
};

type Pallet = {
  id: number;
  state: string;
  barcode: Barcode;
  items: Item[];
};

type Warehouse = {
  id: number;
  name: string;
};

type Transfer = {
  id: number;
  state: string;
  origin: Warehouse;
  destination: Warehouse;
  pallets: Pallet[];
};

type Form = {
  originID: number;
  destinationID: number;
  palletIDs: number[];
};

export const TransferForm: FC<{
  error?: string;
  loading?: boolean;
  transfer: Transfer;
  onSave(_: Form): void;
}> = ({ error, loading, transfer, onSave }) => {
  const [originID, setOriginID] = useState<number | undefined>(transfer.origin.id);
  const [destinationID, setDestinationID] = useState<number | undefined>(transfer.destination.id);
  const [palletIDs, setPalletIDs] = useState<Set<number>>(() => new Set(transfer.pallets.map((pallet) => pallet.id)));

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!originID || !destinationID) return;
    onSave({ originID, destinationID, palletIDs: Array.from(palletIDs) });
  };

  return (
    <form onSubmit={onSubmit}>
      {error && <Alert style="danger">{error}</Alert>}

      <Panel>
        <Panel.Body>
          <WarehouseSelectFormGroup
            disabled
            id="transfer-origin"
            name="transfer[origin]"
            placeholder="- Origin -"
            label="Origin"
            value={originID ? String(originID) : undefined}
            onChange={(value) => setOriginID(value ? Number(value) : undefined)}
          />

          <WarehouseSelectFormGroup
            disabled={transfer.state !== 'approved'}
            id="transfer-destination"
            name="transfer[destination]"
            placeholder="- Destination -"
            label="Destination"
            value={destinationID ? String(destinationID) : undefined}
            onChange={(value) => setDestinationID(value ? Number(value) : undefined)}
          />
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Pluralize count={palletIDs.size} singular="Selection" plural="Selections" />
          </Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Table>
            <tbody>
              {transfer.pallets.map((pallet) => (
                <tr key={pallet.id}>
                  <td className="text-left col-zero col-nowrap">
                    <Checkbox>
                      <input
                        type="checkbox"
                        disabled={pallet.state === 'enroute'}
                        checked={palletIDs.has(pallet.id)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setPalletIDs((palletIDs) => {
                            const changedIDs = new Set(palletIDs);
                            if (checked) changedIDs.add(pallet.id);
                            else changedIDs.delete(pallet.id);
                            return changedIDs;
                          });
                        }}
                      />
                      <span>#{pallet.barcode.value}</span>
                      <strong>({pallet.state})</strong>
                    </Checkbox>
                  </td>
                  <td className="text-left col-fill col-nowrap">
                    <Box.Flex gap="4px">
                      {pallet.items.map((item) => {
                        const hero = item.images[item.hero];
                        return (
                          <Picture
                            key={item.id}
                            source={hero && hero.state === 'uploaded' ? hero.source : undefined}
                            width={40}
                            height={40}
                          />
                        );
                      })}
                    </Box.Flex>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel.Body>
        <Panel.Footer align="right">
          <NavLinkButton kind="default" to={`/transfers/${transfer.id}`} disabled={loading}>
            Cancel
          </NavLinkButton>
          <Button kind="primary" type="submit" loading={loading}>
            Save
          </Button>
        </Panel.Footer>
      </Panel>
    </form>
  );
};
